/**
 * ============================================================================
 * Number fields are typed, never stepped.
 * ============================================================================
 *
 * The spinner arrows are hidden in `base.css`, but a native `<input type="number">` still steps on
 * two other gestures: the ArrowUp / ArrowDown keys, and the mouse wheel while the field has focus.
 * The wheel is the dangerous one — a user who clicks into Expected Annual Business and then scrolls
 * the page changes the amount without seeing it happen.
 *
 * Installed once at start-up, at the document, so every number field in the app is covered —
 * including ones added later — without each call site having to remember it.
 */

const isNumberInput = (target: EventTarget | null): target is HTMLInputElement =>
  target instanceof HTMLInputElement && target.type === 'number';

export function installNumberInputGuard(root: Document = document): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && isNumberInput(event.target)) {
      event.preventDefault();
    }
  };

  /*
   * ⚠️ BLURRED, NOT `preventDefault`. Cancelling the wheel would also cancel the page scroll, and the
   * form would feel stuck whenever the pointer sat over a focused amount. Taking focus away is what
   * stops the browser stepping the value — it only steps a focused field — and the scroll the user
   * asked for carries on. Only a FOCUSED number field is touched.
   */
  const onWheel = (event: WheelEvent) => {
    if (isNumberInput(event.target) && root.activeElement === event.target) {
      event.target.blur();
    }
  };

  root.addEventListener('keydown', onKeyDown, true);
  root.addEventListener('wheel', onWheel, { capture: true, passive: true });

  return () => {
    root.removeEventListener('keydown', onKeyDown, true);
    root.removeEventListener('wheel', onWheel, { capture: true });
  };
}
